<template>
  <v-container>
    <LoginRequired/>
    <ErrorDialog
        :show="isError"
        :errorContent="errorContent"
    />

    <template v-if="isLoaded">
      <v-alert
          color="primary"
          elevation="24"
          icon="mdi-information-outline"
          type="info"
      >
        Von Lager {{ warehouseFrom }}: {{ fromBinLocationDisplay }} | Nach Lager {{ warehouseTo }}: {{ toBinLocationDisplay }}
      </v-alert>

      <v-card outlined class="mb-4">
        <v-card-text>
          <p class="mb-2"><strong>Quelle</strong></p>
          <p class="mb-0">Lager: {{ warehouseFrom }}</p>
          <p class="mb-4">Lagerplatz: {{ fromBinLocationDisplay }}</p>

          <p class="mb-2"><strong>Ziel</strong></p>
          <p class="mb-0">Lager: {{ warehouseTo }}</p>
          <p class="mb-0">Lagerplatz: {{ toBinLocationDisplay }}</p>
        </v-card-text>
      </v-card>

      <v-card outlined class="mb-4">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" sm="8">
              <strong>Umbuchungsvorschau</strong>
              <div class="caption">Artikelnummer, Name und Menge vom Quell-Lagerplatz</div>
            </v-col>
            <v-col cols="12" sm="4" class="text-sm-right">
              <v-btn
                  outlined
                  color="primary"
                  :loading="isLoadingRelocationItems"
                  @click="toggleRelocationItems"
              >
                {{ showRelocationItems ? 'Ausblenden' : 'Anzeigen' }}
              </v-btn>
            </v-col>
          </v-row>

          <template v-if="showRelocationItems">
            <v-alert
                v-if="relocationItemsLoaded && relocationItems.length === 0"
                dense
                outlined
                type="info"
            >
              Keine umlagerbaren Artikel auf dem Quell-Lagerplatz gefunden.
            </v-alert>

            <div v-else-if="relocationItems.length > 0">
              <div class="caption mb-2">
                Positionen: {{ relocationItems.length }} | Gesamtmenge: {{ relocationItemsTotalQuantity }}
              </div>
              <v-simple-table dense class="relocation-preview-table">
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th class="text-left preview-col-number">Artikelnummer</th>
                    <th class="text-left preview-col-name">Name</th>
                    <th class="text-right preview-col-quantity">Menge</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr
                      v-for="item in relocationItems"
                      :key="item.articleDetailId + '-' + item.articleNumber"
                  >
                    <td class="preview-col-number">{{ item.articleNumber }}</td>
                    <td class="preview-col-name">{{ item.name }}</td>
                    <td class="text-right preview-col-quantity">{{ item.quantity }}</td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <v-alert
          v-if="targetBinLocationIsOccupied"
          dense
          outlined
          type="info"
      >
        Hinweis: Der Ziellagerplatz ist nicht leer. Die Artikel vom Quell-Lagerplatz werden zum vorhandenen Bestand hinzugefügt.
      </v-alert>

      <template v-if="!confirmed">
        <v-row class="mt-4">
          <v-spacer/>
          <v-btn
              color="success"
              large
              :disabled="isSubmitting"
              @click="confirmRelocation"
          >
            Bestätigen
          </v-btn>
          <v-spacer/>
        </v-row>
      </template>

      <template v-else>
        <v-alert
            dense
            outlined
            type="success"
        >
          Die komplette Umlagerung wurde erfolgreich bestätigt.
        </v-alert>
      </template>
    </template>

    <v-container v-else>
      <v-progress-circular
          indeterminate
          color="primary"
      ></v-progress-circular>
    </v-container>

    <VerticalSpacer/>
    <v-row v-if="!confirmed">
      <v-spacer/>
      <v-btn
          :to="{
            name: 'rearrange.entire_bin_location.select_bin_location_to',
            params: {
              from: warehouseFrom,
              to: warehouseTo,
              fromBinLocation: fromBinLocation
            },
            query: {
              fromDisplayName: fromBinLocationDisplay
            }
          }"
          outlined
          color="primary"
      >
        Zurück
      </v-btn>
      <v-spacer/>
    </v-row>
    <v-row v-else>
      <v-spacer/>
      <v-btn
          color="success"
          :to="{ name: 'rearrange.entire_bin_location.select_warehouse_from_to' }"
      >
        OK
      </v-btn>
      <v-spacer/>
    </v-row>
  </v-container>
</template>

<script>
import VerticalSpacer from "../../Others/VerticalSpacer";
import LoginRequired from "../../Others/LoginRequired";
import ErrorDialog from "../../Others/ErrorDialog";

export default {
  components: {ErrorDialog, LoginRequired, VerticalSpacer},
  data: () => ({
    warehouseFrom: '',
    warehouseTo: '',
    fromBinLocation: '',
    toBinLocation: '',
    fromBinLocationDisplay: '',
    toBinLocationDisplay: '',
    targetBinLocationIsOccupied: false,
    showRelocationItems: false,
    isLoadingRelocationItems: false,
    relocationItemsLoaded: false,
    relocationItems: [],
    isLoaded: false,
    confirmed: false,
    isSubmitting: false,
    isError: false,
    errorContent: '',
  }),
  computed: {
    relocationItemsTotalQuantity() {
      return this.relocationItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    },
  },
  created() {
    this.$root.$on('onCloseErrorDialog', this.handleCloseErrorDialog);

    this.warehouseFrom = this.$route.params.from;
    this.warehouseTo = this.$route.params.to;
    this.fromBinLocation = this.$route.params.fromBinLocation;
    this.toBinLocation = this.$route.params.toBinLocation;

    this.fromBinLocationDisplay = this.fromBinLocation;
    this.toBinLocationDisplay = this.toBinLocation;

    this.loadDisplayNames();
  },
  beforeDestroy() {
    this.$root.$off('onCloseErrorDialog', this.handleCloseErrorDialog);
  },
  methods: {
    async loadDisplayNames() {
      this.isLoaded = false;
      try {
        const [fromResponse, toResponse] = await Promise.all([
          this.$axios.get(`binLocationSelection?warehouseCode=${encodeURIComponent(this.warehouseFrom)}&scanAction=1&code=${encodeURIComponent(this.fromBinLocation)}&includePalettes=1`),
          this.$axios.get(`binLocationSelection?warehouseCode=${encodeURIComponent(this.warehouseTo)}&scanAction=1&code=${encodeURIComponent(this.toBinLocation)}&includePalettes=1`),
        ]);

        if (fromResponse && fromResponse.data && fromResponse.data.success && Array.isArray(fromResponse.data.data) && fromResponse.data.data[0]) {
          this.fromBinLocationDisplay = String(fromResponse.data.data[0].displayName || fromResponse.data.data[0].code || this.fromBinLocation).trim();
        }
        if (toResponse && toResponse.data && toResponse.data.success && Array.isArray(toResponse.data.data) && toResponse.data.data[0]) {
          const toBinLocationData = toResponse.data.data[0];
          this.toBinLocationDisplay = String(toBinLocationData.displayName || toBinLocationData.code || this.toBinLocation).trim();
          this.targetBinLocationIsOccupied = !toBinLocationData.isEmpty;
        }
      } catch (error) {
        this.setError(error && error.message ? error.message : 'Daten konnten nicht vollständig geladen werden.');
      } finally {
        this.isLoaded = true;
      }
    },
    async toggleRelocationItems() {
      this.showRelocationItems = !this.showRelocationItems;
      if (!this.showRelocationItems || this.relocationItemsLoaded) {
        return;
      }
      await this.loadRelocationItems();
    },
    async loadRelocationItems() {
      this.isLoadingRelocationItems = true;
      try {
        const response = await this.$axios.get(
            `binLocationInfo?warehouseCode=${encodeURIComponent(this.warehouseFrom)}&binLocationCode=${encodeURIComponent(this.fromBinLocation)}`
        );
        if (!(response && response.data && response.data.success)) {
          throw new Error((response && response.data && response.data.message) ? response.data.message : 'Umlagerungsvorschau konnte nicht geladen werden.');
        }

        const items = Array.isArray(response.data.data) ? response.data.data : [];
        this.relocationItems = items.map(item => ({
          articleDetailId: Number(item.articleDetailId || 0),
          articleNumber: item.ordernumber || '-',
          name: item.name || '-',
          quantity: Number(item.actualStock || 0),
        })).filter(item => item.quantity > 0);
        this.relocationItemsLoaded = true;
      } catch (error) {
        this.setError(error && error.message ? error.message : 'Umlagerungsvorschau konnte nicht geladen werden.');
      } finally {
        this.isLoadingRelocationItems = false;
      }
    },
    async confirmRelocation() {
      if (this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;
      this.isError = false;
      this.errorContent = '';

      const address = 'binLocationRelocation/' + this.$uuid.v4();
      const payload = [{
        sourceWarehouseCode: this.warehouseFrom,
        sourceBinLocationCode: this.fromBinLocation,
        destinationWarehouseCode: this.warehouseTo,
        destinationBinLocationCode: this.toBinLocation,
        bookingLog: {
          payload: {
            sourceWarehouseCode: this.warehouseFrom,
            sourceBinLocationCode: this.fromBinLocation,
            destinationWarehouseCode: this.warehouseTo,
            destinationBinLocationCode: this.toBinLocation,
          },
          payloadUrl: address,
          place: 'Kompletten Lagerplatz umlagern',
          userId: localStorage.getItem('userId') ? Number(localStorage.getItem('userId')) : null,
          userName: localStorage.getItem('userName') ? localStorage.getItem('userName') : null,
          binLocationCodeFrom: this.fromBinLocation,
          binLocationCodeTo: this.toBinLocation,
          warehouseCodeFrom: this.warehouseFrom,
          warehouseCodeTo: this.warehouseTo,
        },
      }];

      try {
        const response = await this.$axios.put(address, payload);
        if (!(response && response.data && response.data.success)) {
          throw new Error((response && response.data && response.data.message) ? response.data.message : 'Umlagerung konnte nicht abgeschlossen werden.');
        }
        this.confirmed = true;
      } catch (error) {
        this.setError(error && error.message ? error.message : 'Umlagerung konnte nicht abgeschlossen werden.');
      } finally {
        this.isSubmitting = false;
      }
    },
    setError(message) {
      this.isError = true;
      this.errorContent = message;
    },
    handleCloseErrorDialog() {
      this.isError = false;
    },
  }
}
</script>

<style scoped>
.relocation-preview-table >>> table {
  width: 100%;
  table-layout: fixed;
}

.preview-col-number {
  width: 34%;
  white-space: nowrap;
}

.preview-col-name {
  width: 46%;
  white-space: normal;
  word-break: break-word;
}

.preview-col-quantity {
  width: 20%;
  white-space: nowrap;
}
</style>
